/**
 * @file Table 基础表格
 * @description 语义化表格结构 + 空态 + 首列 selection + 固定列；滚动由 ScrollArea 承接（hover 显示滑块）。
 *
 * @whenToUse 后台列表；可再包 DataTable。
 * @whenNotToUse 复杂卡片流布局。
 */

import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react'
import { cn } from '@/lib/cn.ts'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Empty } from '@/components/ui/empty.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { Tooltip } from './tooltip'

/** 固定列方向 */
export type TableFixed = 'left' | 'right'

/** 选择列默认宽度（与 w-10 一致） */
export const TABLE_SELECTION_WIDTH = 40

export type TableStickyProps = {
  /** 固定到左侧或右侧 */
  fixed?: TableFixed
  /** sticky 偏移（px）；多列固定时按宽度累加 */
  stickyOffset?: number
  /** 同侧最内侧固定列（横向滚动后展示内侧阴影） */
  fixedEdge?: boolean
  /** 固定列宽（px）；同时设 min/max 为该值（除非另传 minWidth / maxWidth） */
  width?: number
  /** 最小列宽（px）；可与 width / maxWidth 组合 */
  minWidth?: number
  /** 最大列宽（px）；可与 width / minWidth 组合 */
  maxWidth?: number
  /**
   * 是否开启内容缩略：单行省略，悬停 Tooltip 展示全文。
   * 未开启时按换行正常展示（保留 `\n`）。
   */
  ellipsis?: boolean
}

/**
 * 组装单元格宽高约束样式
 * @param width 固定宽
 * @param minWidth 最小宽
 * @param maxWidth 最大宽
 */
function cellWidthStyle(
  width: number | undefined,
  minWidth: number | undefined,
  maxWidth: number | undefined,
): CSSProperties | undefined {
  if (
    width === undefined &&
    minWidth === undefined &&
    maxWidth === undefined
  ) {
    return undefined
  }
  const style: CSSProperties = {}
  if (width !== undefined) {
    style.width = width
    style.minWidth = minWidth ?? width
    style.maxWidth = maxWidth ?? width
  } else {
    if (minWidth !== undefined) style.minWidth = minWidth
    if (maxWidth !== undefined) style.maxWidth = maxWidth
  }
  return style
}

function stickyStyle(
  fixed: TableFixed | undefined,
  stickyOffset: number,
  width: number | undefined,
  minWidth: number | undefined,
  maxWidth: number | undefined,
): CSSProperties | undefined {
  const widthStyle = cellWidthStyle(width, minWidth, maxWidth)
  if (!fixed && !widthStyle) return undefined
  const style: CSSProperties = { ...widthStyle }
  if (fixed === 'left') style.left = stickyOffset
  if (fixed === 'right') style.right = stickyOffset
  return style
}

function stickyClassName(
  fixed: TableFixed | undefined,
  fixedEdge: boolean | undefined,
  variant: 'head' | 'cell',
): string | undefined {
  if (!fixed) return undefined
  return cn(
    'ui-table-sticky sticky',
    variant === 'head' ? 'z-30' : 'z-10',
    fixed === 'left' && fixedEdge && 'ui-table-sticky-left',
    fixed === 'right' && fixedEdge && 'ui-table-sticky-right',
  )
}

/**
 * 单元格内容展示：
 * - enabled：单行缩略，悬停 Tooltip 展示全文（保留换行）
 * - 未开启：正常换行展示
 */
function CellTruncate({
  enabled,
  children,
}: {
  enabled: boolean
  children: ReactNode
}) {
  if (!enabled) {
    return (
      <div className="whitespace-pre-wrap wrap-break-word">
        {children}
      </div>
    )
  }

  return (
    <Tooltip
      interactive
      placement="top-left"
      className="block w-full min-w-0 max-w-full"
      content={(
        <ScrollArea
          orientation="vertical"
          showOn="hover"
          className="max-w-80"
          viewportClassName="h-auto max-h-40"
        >
          <div className="whitespace-pre-wrap wrap-break-word">
            {children}
          </div>
        </ScrollArea>
      )}
    >
      <div className="min-w-0 truncate">{children}</div>
    </Tooltip>
  )
}

export function Table({
  className,
  children,
  scrollerClassName,
  ...rest
}: HTMLAttributes<HTMLTableElement> & {
  /** 滚动外壳 className（ScrollArea 根节点） */
  scrollerClassName?: string
}) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [pingLeft, setPingLeft] = useState(false)
  const [pingRight, setPingRight] = useState(false)

  useLayoutEffect(() => {
    const scroller = viewportRef.current
    if (!scroller) return

    const updatePing = () => {
      setPingLeft(scroller.scrollLeft > 0)
      setPingRight(
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1,
      )
    }

    updatePing()
    const observer = new ResizeObserver(updatePing)
    observer.observe(scroller)
    const table = scroller.querySelector('table')
    if (table) observer.observe(table)
    return () => observer.disconnect()
  }, [])

  return (
    <ScrollArea
      orientation="both"
      showOn="hover"
      className={cn(
        'w-full min-w-0 rounded-lg border border-border bg-sidebar',
        scrollerClassName,
      )}
      viewportRef={viewportRef}
      viewportClassName={cn(
        'ui-table-scroller',
        pingLeft && 'ui-table-ping-left',
        pingRight && 'ui-table-ping-right',
      )}
      onViewportScroll={(event) => {
        const scroller = event.currentTarget
        setPingLeft(scroller.scrollLeft > 0)
        setPingRight(
          scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1,
        )
      }}
    >
      <table
        className={cn(
          'ui-table w-full border-separate border-spacing-0 text-left text-sm',
          className,
        )}
        {...rest}
      >
        {children}
      </table>
    </ScrollArea>
  )
}

export function TableHeader({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn('bg-surface text-muted', className)}
      {...rest}
    >
      {children}
    </thead>
  )
}

export function TableBody({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={cn('bg-sidebar', className)} {...rest}>
      {children}
    </tbody>
  )
}

export function TableRow({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn(
        'ui-table-row transition-colors duration-150',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  )
}

export type TableHeadProps = ThHTMLAttributes<HTMLTableCellElement> &
  TableStickyProps

export function TableHead({
  className,
  children,
  fixed,
  stickyOffset = 0,
  fixedEdge,
  width,
  minWidth,
  maxWidth,
  ellipsis = false,
  style,
  ...rest
}: TableHeadProps) {
  return (
    <th
      className={cn(
        'px-3 py-2.5 font-medium border-b border-border',
        stickyClassName(fixed, fixedEdge, 'head'),
        className,
      )}
      style={{
        ...stickyStyle(fixed, stickyOffset, width, minWidth, maxWidth),
        ...style,
      }}
      {...rest}
    >
      <CellTruncate enabled={ellipsis}>{children}</CellTruncate>
    </th>
  )
}

export type TableCellProps = TdHTMLAttributes<HTMLTableCellElement> &
  TableStickyProps

export function TableCell({
  className,
  children,
  fixed,
  stickyOffset = 0,
  fixedEdge,
  width,
  minWidth,
  maxWidth,
  ellipsis = false,
  style,
  ...rest
}: TableCellProps) {
  return (
    <td
      className={cn(
        'border-b border-border px-3 py-2 text-foreground',
        stickyClassName(fixed, fixedEdge, 'cell'),
        className,
      )}
      style={{
        ...stickyStyle(fixed, stickyOffset, width, minWidth, maxWidth),
        ...style,
      }}
      {...rest}
    >
      <CellTruncate enabled={ellipsis}>{children}</CellTruncate>
    </td>
  )
}

export type TableEmptyProps = {
  colSpan: number
  title?: ReactNode
  description?: ReactNode
}

export function TableEmpty({
  colSpan,
  title = '暂无数据',
  description,
}: TableEmptyProps) {
  return (
    <tr>
      <td colSpan={colSpan}>
        <Empty title={title} description={description} />
      </td>
    </tr>
  )
}

export type TableSelectionHeadProps = TableStickyProps & {
  /** 是否全选 */
  checked?: boolean
  /** 部分选中 */
  indeterminate?: boolean
  /** 全选变更 */
  onCheckedChange?: (checked: boolean) => void
  /** 无障碍文案 */
  'aria-label'?: string
}

/**
 * 表头首列：全选 Checkbox
 */
export function TableSelectionHead({
  checked = false,
  indeterminate = false,
  onCheckedChange,
  'aria-label': ariaLabel = '全选',
  fixed,
  stickyOffset = 0,
  fixedEdge,
  width = TABLE_SELECTION_WIDTH,
}: TableSelectionHeadProps) {
  return (
    <TableHead
      className="px-3"
      fixed={fixed}
      stickyOffset={stickyOffset}
      fixedEdge={fixedEdge}
      width={width}
    >
      <Checkbox
        aria-label={ariaLabel}
        checked={checked}
        indeterminate={indeterminate}
        onChange={(event) => onCheckedChange?.(event.target.checked)}
      />
    </TableHead>
  )
}

export type TableSelectionCellProps = TableStickyProps & {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  'aria-label'?: string
}

/**
 * 表体首列：行选择 Checkbox
 */
export function TableSelectionCell({
  checked = false,
  onCheckedChange,
  'aria-label': ariaLabel = '选择行',
  fixed,
  stickyOffset = 0,
  fixedEdge,
  width = TABLE_SELECTION_WIDTH,
}: TableSelectionCellProps) {
  return (
    <TableCell
      className="px-3"
      fixed={fixed}
      stickyOffset={stickyOffset}
      fixedEdge={fixedEdge}
      width={width}
    >
      <Checkbox
        aria-label={ariaLabel}
        checked={checked}
        onChange={(event) => onCheckedChange?.(event.target.checked)}
      />
    </TableCell>
  )
}

/**
 * 根据列宽累加计算左/右 sticky 偏移
 * @param widths - 同侧固定列宽度（从外侧到内侧顺序：左固定从左到右，右固定从右到左）
 */
export function computeStickyOffsets(widths: number[]): number[] {
  const offsets: number[] = []
  let acc = 0
  for (const w of widths) {
    offsets.push(acc)
    acc += w
  }
  return offsets
}
